import { useEffect, useRef } from "react";
import type { CmmdWorkIntake } from "@cmmd-forge/contracts";
import { fetchPrimaryCmmdWorkIntake } from "~/environments/primary";
import { cmmdApiClient, CmmdApiError } from "~/environments/cmmdApi";
import { isAuthFailureIntake } from "./cmmdAuthFailureSignal";
import { useFeatureFlag } from "./useFeatureFlag";
import {
  clearPersistedProjectEvictions,
  parseCmmdWorkIntakeEvictionStorageChange,
  readCachedCmmdWorkIntake,
  readCmmdWorkIntakeEvictionGeneration,
  writeCachedCmmdWorkIntakeIfGenerationCurrent,
} from "../cmmdWorkIntakeCache";
import { cmmdRealtimeClient } from "../environments/cmmdRealtimeClient";
import type { CmmdEvent } from "../environments/cmmdRealtimeClient";
import {
  cmmdWorkIntakeResource,
  readCmmdWorkIntakeSnapshot,
  subscribeCmmdWorkIntake,
} from "~/cmmd/workIntakeResource";
import {
  CMMD_STARTUP_AUTH_MAX_ATTEMPTS,
  CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
  resolveCmmdStartupAuthFailureDecision,
} from "~/startup/startupReadiness";

const REFETCH_INTERVAL_MS = 45_000;
const AUTHORITATIVE_SYNC_TIMEOUT_MS = 30_000;

interface RunCmmdSyncInput {
  readonly isActive?: () => boolean;
  readonly forceFetch?: boolean;
  /** True for user-visible foreground refreshes that should show Syncing. */
  readonly activeFetch?: boolean;
  /** When true, still hit network even if cache is fresh+connected. */
  readonly validateFreshCache?: boolean;
  /** Bypass the serving runtime cache after a durable permanent deletion. */
  readonly bypassServerCache?: boolean;
  readonly fetchCmmdWorkIntake?: (options?: {
    readonly forceFresh: boolean;
  }) => Promise<CmmdWorkIntake>;
  readonly readCachedCmmdWorkIntake?: () => ReturnType<typeof readCachedCmmdWorkIntake>;
  readonly writeCachedCmmdWorkIntake?: (intake: CmmdWorkIntake) => void;
  readonly readEvictionGeneration?: () => string | null;
  readonly onAuthExpired?: () => void | Promise<void>;
  readonly startupAuthMaxAttempts?: number;
  readonly startupAuthRetryDelayMs?: number;
  readonly authoritativeTimeoutMs?: number;
  readonly onDataRequestStarted?: (version: number) => void;
  readonly sleep?: (delayMs: number) => Promise<void>;
}

function resolveCmmdSyncErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Failed to load CMMD data.";
}

function readStatusCode(cause: unknown): number | null {
  if (typeof cause !== "object" || cause === null) return null;
  const status = (cause as { status?: unknown }).status;
  if (typeof status !== "number" || !Number.isFinite(status)) return null;
  return status;
}

function isAuthFailureError(cause: unknown): boolean {
  if (cause instanceof CmmdApiError) {
    return cause.kind === "auth";
  }
  const status = readStatusCode(cause);
  return status === 401 || status === 403;
}

async function handleCmmdAuthExpired(): Promise<void> {
  // Product rule: CMMD REST/work-intake auth failure is a full product login.
  // Shared with cmmdApiClient.onAuthExpired (cmmdAuthRedirect.ts).
  const { handleCmmdAuthExpired: redirectOnCmmdAuthExpired } =
    await import("~/environments/cmmdApi/cmmdAuthRedirect");
  await redirectOnCmmdAuthExpired(401);
}

function sleepFor(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

let authoritativeSyncsInFlight = 0;

function waitForBoundedAuthoritativeSync(
  request: Promise<void>,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), timeoutMs);
    void request.then(() => {
      clearTimeout(timeout);
      resolve(true);
    });
  });
}

async function runAuthoritativeCmmdSync(input: RunCmmdSyncInput): Promise<void> {
  authoritativeSyncsInFlight += 1;
  try {
    let authoritativeVersion: number | null = null;
    const request = runCmmdSync({
      ...input,
      onDataRequestStarted: (version) => {
        authoritativeVersion = version;
      },
    });
    const completed = await waitForBoundedAuthoritativeSync(
      request,
      input.authoritativeTimeoutMs ?? AUTHORITATIVE_SYNC_TIMEOUT_MS,
    );
    if (
      !completed &&
      authoritativeVersion !== null &&
      cmmdWorkIntakeResource.isDataVersionCurrent(authoritativeVersion)
    ) {
      cmmdWorkIntakeResource.beginDataRequest();
      cmmdWorkIntakeResource.setBackgroundRefreshError(
        "CMMD project refresh timed out. Retry to load canonical project data.",
      );
    }
  } finally {
    authoritativeSyncsInFlight -= 1;
  }
}

async function runCmmdSyncWithAuthority(input: RunCmmdSyncInput = {}): Promise<void> {
  if (input.bypassServerCache === true) {
    await runAuthoritativeCmmdSync(input);
    return;
  }
  if (authoritativeSyncsInFlight > 0) return;
  await runCmmdSync(input);
}

async function runCmmdSync(input: RunCmmdSyncInput): Promise<void> {
  const {
    isActive = () => true,
    forceFetch = false,
    activeFetch = false,
    validateFreshCache = false,
    bypassServerCache = false,
    fetchCmmdWorkIntake = fetchPrimaryCmmdWorkIntake,
    readCachedCmmdWorkIntake: readCached = () => readCachedCmmdWorkIntake(),
    writeCachedCmmdWorkIntake: writeCached,
    readEvictionGeneration = readCmmdWorkIntakeEvictionGeneration,
    onAuthExpired = handleCmmdAuthExpired,
    startupAuthMaxAttempts = CMMD_STARTUP_AUTH_MAX_ATTEMPTS,
    startupAuthRetryDelayMs = CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
    sleep = sleepFor,
    onDataRequestStarted,
  } = input;
  const dataVersion = cmmdWorkIntakeResource.beginDataRequest();
  onDataRequestStarted?.(dataVersion);
  let evictionGeneration = readEvictionGeneration();
  const isCurrent = () =>
    isActive() &&
    cmmdWorkIntakeResource.isDataVersionCurrent(dataVersion) &&
    readEvictionGeneration() === evictionGeneration;

  const cached = forceFetch ? { state: "missing", record: null } : readCached();
  const hasCachedData = (cached.state === "fresh" || cached.state === "stale") && cached.record;
  const shouldDoBlockingFetch =
    !forceFetch && (cached.state === "missing" || cached.state === "expired");
  const shouldSkipFetchForFreshCache =
    !validateFreshCache && cached.state === "fresh" && cached.record?.intake.connected === true;

  if (hasCachedData) {
    if (!isCurrent()) return;
    cmmdWorkIntakeResource.setDataIfCurrent(cached.record.intake, dataVersion);
  }

  if (shouldSkipFetchForFreshCache) {
    return;
  }

  if (shouldDoBlockingFetch || activeFetch) {
    if (!isCurrent()) return;
    cmmdWorkIntakeResource.setLoading(true);
  }

  let authFailureAttempt = 0;

  for (;;) {
    try {
      const intake = await fetchCmmdWorkIntake({
        forceFresh: bypassServerCache,
      });
      if (!isCurrent()) return;
      if (isAuthFailureIntake(intake)) {
        authFailureAttempt += 1;
        const decision = resolveCmmdStartupAuthFailureDecision({
          failedAttempt: authFailureAttempt,
          maxAttempts: startupAuthMaxAttempts,
          retryDelayMs: startupAuthRetryDelayMs,
        });
        if (decision.kind === "retry") {
          if (shouldDoBlockingFetch || activeFetch) {
            cmmdWorkIntakeResource.setLoading(true);
          }
          await sleep(decision.delayMs);
          if (!isCurrent()) return;
          continue;
        }
        await onAuthExpired();
        return;
      }
      clearPersistedProjectEvictions(intake.projects.map((project) => project.id));
      evictionGeneration = readEvictionGeneration();
      if (writeCached) {
        writeCached(intake);
      } else {
        writeCachedCmmdWorkIntakeIfGenerationCurrent(intake, evictionGeneration);
      }
      if (!isCurrent()) return;
      cmmdWorkIntakeResource.setDataIfCurrent(intake, dataVersion);
      return;
    } catch (cause) {
      if (!isCurrent()) return;
      if (isAuthFailureError(cause)) {
        authFailureAttempt += 1;
        const decision = resolveCmmdStartupAuthFailureDecision({
          failedAttempt: authFailureAttempt,
          maxAttempts: startupAuthMaxAttempts,
          retryDelayMs: startupAuthRetryDelayMs,
        });
        if (decision.kind === "retry") {
          if (shouldDoBlockingFetch || activeFetch) {
            cmmdWorkIntakeResource.setLoading(true);
          }
          await sleep(decision.delayMs);
          if (!isCurrent()) return;
          continue;
        }
        await onAuthExpired();
        return;
      }
      const message = resolveCmmdSyncErrorMessage(cause);
      if (forceFetch) {
        cmmdWorkIntakeResource.setBackgroundRefreshError(message);
        return;
      }
      if (shouldDoBlockingFetch || !hasCachedData) {
        cmmdWorkIntakeResource.setError(message);
        return;
      }
      cmmdWorkIntakeResource.setBackgroundRefreshError(message);
      return;
    }
  }
}

export { runCmmdSyncWithAuthority as runCmmdSync };

/**
 * Mount once (e.g. in the root layout) to sync CMMD data into the canonical
 * work-intake resource. Subsequent components read from the resource; they
 * don't fetch.
 */
export function useCmmdSync(): void {
  // Guard against StrictMode double-mount firing the effect twice.
  const bootstrappedRef = useRef(false);
  // Baseline CMMD sync is no longer feature-gated. Desktop must always load
  // cloud/local work context when authenticated; realtime push remains gated
  // because it is an optimization over the polling/refetch path.
  const realtimeWsEnabled = useFeatureFlag("cmmd_realtime_ws");

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    const controller = new AbortController();
    let active = true;
    let refetchInterval: ReturnType<typeof setInterval> | null = null;
    let wsUnsubscribe: (() => void) | null = null;
    let storeUnsubscribe: (() => void) | null = null;
    let storageUnsubscribe: (() => void) | null = null;

    const isActive = () => active;

    async function onAuthExpired(): Promise<void> {
      if (!active) return;
      active = false;
      controller.abort();
      if (refetchInterval !== null) {
        clearInterval(refetchInterval);
        refetchInterval = null;
      }
      wsUnsubscribe?.();
      cmmdRealtimeClient.disconnect();
      await handleCmmdAuthExpired();
    }

    async function doFetch(options?: {
      force?: boolean;
      active?: boolean;
      validateFreshCache?: boolean;
      authoritative?: boolean;
    }): Promise<void> {
      await runCmmdSync({
        isActive,
        forceFetch: options?.force ?? false,
        activeFetch: options?.active ?? false,
        validateFreshCache: options?.validateFreshCache ?? false,
        bypassServerCache: options?.authoritative ?? false,
        onAuthExpired,
      });
    }

    function handleCmmdEvent(event: CmmdEvent): void {
      if (!active) return;
      const current = readCmmdWorkIntakeSnapshot();

      switch (event.type) {
        case "TASK_ASSIGNED":
        case "TASK_COMPLETED":
        case "TASK_COMMENTED": {
          const updatedTaskId = event.taskId;
          const taskExists = current.tasks.some((task) => task.id === updatedTaskId);
          if (!taskExists) {
            void doFetch({ force: true });
            return;
          }
          const updatedTasks = current.tasks.map((task) => {
            if (task.id !== updatedTaskId) return task;
            if (event.type === "TASK_COMPLETED") {
              return Object.assign({}, task, { status: "done" });
            }
            if (event.type === "TASK_ASSIGNED") {
              return Object.assign({}, task, { assigneeId: String(event.assigneeId) });
            }
            return task;
          });
          cmmdWorkIntakeResource.replaceTasks(updatedTasks);
          break;
        }

        case "PROJECT_UPDATED":
        case "PROJECT_CREATED": {
          void doFetch({ force: true });
          break;
        }

        case "BRAIN_DOCUMENT_CREATED":
        case "COMMENT_CREATED": {
          break;
        }
      }
    }

    function handleWorkIntakeEviction(event: StorageEvent): void {
      if (!active) return;
      const change = parseCmmdWorkIntakeEvictionStorageChange(event.key, event.newValue);
      switch (change.kind) {
        case "unrelated":
          return;
        case "projects_removed":
          cmmdWorkIntakeResource.applyExternalProjectRemovals(change.projectIds);
          break;
        case "cache_removed":
        case "malformed":
          // A malformed fence or fail-closed cache removal is unsafe to ignore.
          // Drop the snapshot and recover from the authenticated server.
          cmmdWorkIntakeResource.clear();
          break;
      }
      cmmdWorkIntakeResource.requestRefetch({ authoritative: true });
    }

    async function connectRealtime(): Promise<void> {
      try {
        const { token, wsUrl } = await cmmdApiClient.getWebSocketToken({
          signal: controller.signal,
        });
        if (!active) return;

        wsUnsubscribe = cmmdRealtimeClient.onEvent(handleCmmdEvent);
        cmmdRealtimeClient.connect(wsUrl, token);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (CmmdApiError.isAuth(error)) {
          await onAuthExpired();
          return;
        }
      }
    }

    void doFetch({ validateFreshCache: true });
    refetchInterval = setInterval(() => {
      void doFetch({ force: true });
    }, REFETCH_INTERVAL_MS);
    if (realtimeWsEnabled) {
      void connectRealtime();
    }
    window.addEventListener("storage", handleWorkIntakeEviction);
    storageUnsubscribe = () => window.removeEventListener("storage", handleWorkIntakeEviction);

    // Watch for on-demand refetch requests (e.g. agent turn completion).
    let lastSeenRefetchAt = readCmmdWorkIntakeSnapshot().refetchRequestedAt;
    storeUnsubscribe = subscribeCmmdWorkIntake(() => {
      const next = readCmmdWorkIntakeSnapshot().refetchRequestedAt;
      if (next !== null && next !== lastSeenRefetchAt && active) {
        lastSeenRefetchAt = next;
        void doFetch({
          force: true,
          active: true,
          authoritative: readCmmdWorkIntakeSnapshot().authoritativeRefetchRequested === true,
        });
      }
    });

    return () => {
      active = false;
      controller.abort();
      if (refetchInterval !== null) clearInterval(refetchInterval);
      wsUnsubscribe?.();
      storeUnsubscribe?.();
      storageUnsubscribe?.();
      cmmdRealtimeClient.disconnect();
      bootstrappedRef.current = false;
    };
    // This effect bootstraps once. Flag toggles after mount don't re-run the effect;
    // a SPA reload picks up realtime changes, matching how PostHog flag changes
    // propagate via reconnect anyway.
  }, [realtimeWsEnabled]);
}
